import React, { FC } from 'react'
import startCase from 'lodash/startCase'
import toLower from 'lodash/toLower'
import { IconButton } from 'transfix-ui/components/IconButton'
import { INLINE_MESSAGE_VARIANTS, InlineMessage } from 'transfix-ui/components/InlineMessage'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { DeleteIcon, LockedIcon } from 'transfix-icons'
import { AdminEmailContextCard } from './AdminEmailContextCard'
import { ISearchedMotorCarrier } from './types'

interface IOnboardingCarrierProps {
  index: number
  motorCarrier: ISearchedMotorCarrier
  /**
   * Whether to show the inline message component
   * @default true
   */
  isMessageVisible?: boolean
  onRemoveCarrier?: (index: number) => void
}

const useOnboardingCarrierStyles = makeStyles(theme => ({
  '@keyframes fadeInLeft': {
    from: {
      opacity: 0,
      transform: 'translateX(-3px)',
    },
    to: {
      opacity: 1,
      transform: 'translateX(0)',
    },
  },
  root: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${brandColors.coolGray1}`,

    // animate opacity and position
    opacity: 0,
    transform: 'translateY(-3px)',
    animation: '$fadeInLeft 250ms ease-in-out forwards',

    // Remove bottom in last element
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  dotNumber: {
    marginBottom: 4,
    color: brandColors.coolGray5,
  },
}))

const LockedIconComponent = () => {
  return <LockedIcon />
}

/**
 * Single carrier item in <OnboardingCarrierList /> container
 */
export const OnboardingCarrierListItem: FC<IOnboardingCarrierProps> = ({
  index,
  isMessageVisible = true,
  motorCarrier,
  onRemoveCarrier,
}) => {
  const removeCarrierHandler = () => {
    if (onRemoveCarrier) {
      onRemoveCarrier(index)
    }
  }

  const classes = useOnboardingCarrierStyles()
  const { dotNumber, legalName = '', maskedAdminEmails = [] } = motorCarrier || {}
  return (
    <Box className={classes.root} data-test='carrier-search-list-item'>
      <Box className={classes.container}>
        <Box>
          <Typography component='div' variant='overline' className={classes.dotNumber}>
            DOT {dotNumber}
          </Typography>
          <Typography component='div'>{startCase(toLower(legalName))}</Typography>
        </Box>
        {onRemoveCarrier && (
          <Box>
            <IconButton
              color='tertiary'
              Icon={DeleteIcon}
              onClick={removeCarrierHandler}
              label='Delete'
              size='large'
            />
          </Box>
        )}
      </Box>
      {maskedAdminEmails.length > 0 && isMessageVisible && (
        <Box mt={2}>
          <InlineMessage
            type={INLINE_MESSAGE_VARIANTS.INFO}
            message={
              <Box>
                The Admin for this carrier will get an email asking to approve you.
                <Box>
                  <AdminEmailContextCard maskedAdminEmails={maskedAdminEmails} />
                </Box>
              </Box>
            }
            title='You’ll need permission to manage this carrier.'
            IconComponent={LockedIconComponent}
          />
        </Box>
      )}
    </Box>
  )
}
